import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./InfoPopover.css";

// Where the popover goes relative to the trigger. Exactly one of top/bottom
// is set: below the trigger anchors `top`, above it anchors `bottom` so the
// popover grows away from the trigger regardless of its unknown height.

const MARGIN_PX = 8;
const GAP_PX = 6;
const MAX_WIDTH_PX = 280;

/**
 * Click to open text popover, portaled to <body> so no ancestor overflow
 * (the product card, the carousel track, the transcript panel) can clip it.
 * Closes on a second trigger click, outside pointerdown, Escape, or any
 * scroll, a fixed position anchor desyncs from a scrolled trigger, so
 * closing beats chasing it.
 *
 * Ported from ai-agent-widget app/src/ui/info-popover.tsx. `preact/compat`'s
 * createPortal becomes react-dom's; the anchoring arithmetic and every
 * dismissal path are unchanged.
 *
 * @param {{ text: string, triggerClass: string, triggerLabel: string, children: React.ReactNode }} props
 */
export function InfoPopover({ text, triggerClass, triggerLabel, children }) {
  const [anchor, setAnchor] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const open = anchor !== null;

  const toggle = () => {
    if (open) {
      setAnchor(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(MAX_WIDTH_PX, vw - MARGIN_PX * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - width / 2, MARGIN_PX),
      vw - MARGIN_PX - width
    );
    const below = rect.top < vh / 2;
    setAnchor(
      below
        ? {
            top: rect.bottom + GAP_PX,
            left,
            width,
            maxHeight: vh - rect.bottom - GAP_PX - MARGIN_PX,
          }
        : {
            bottom: vh - rect.top + GAP_PX,
            left,
            width,
            maxHeight: rect.top - GAP_PX - MARGIN_PX,
          }
    );
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setAnchor(null);
    const onPointerDown = (e) => {
      const target = e.target;
      if (
        popoverRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
        return;
      close();
    };
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      // Capture phase + stopPropagation: dismissing the popover is the
      // narrower intent and must win over any document level Escape handler
      // further up (e.g. a modal closing behind it).
      e.stopPropagation();
      close();
    };
    const onScroll = (e) => {
      // Scroll closes is about ANCHOR desync (the transcript panel, the
      // carousel track). The popover's own overflow-y scroll moves nothing
      // the anchor depends on and is how text taller than maxHeight gets
      // read, never close on it.
      if (popoverRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("scroll", onScroll, true);
    // A resize also strands the fixed position anchor, close, don't chase.
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClass}
        aria-label={triggerLabel}
        aria-expanded={open}
        onClick={toggle}
      >
        {children}
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="transcript-info-popover"
            role="dialog"
            aria-label={triggerLabel}
            style={{
              top: anchor.top !== undefined ? `${anchor.top}px` : "",
              bottom: anchor.bottom !== undefined ? `${anchor.bottom}px` : "",
              left: `${anchor.left}px`,
              width: `${anchor.width}px`,
              maxHeight: `${anchor.maxHeight}px`,
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}
